import { useState } from 'react'
import { ReviewRow } from '@/types'
import { RiskBadge } from './RiskBadge'
import { formatWording } from '@/lib/utils'
import { REVIEW_SECTION_MIME } from '@/lib/chat-attachments'
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react'

interface ReviewTableProps {
  rows: ReviewRow[]
}

const thClass =
  "sticky top-0 z-10 bg-[var(--surface-sunken)] px-2.5 py-2 text-[10px] font-medium uppercase tracking-wide text-[var(--lm-muted)] shadow-[inset_0_-1px_0_0_var(--lm-border-subtle)] dark:bg-slate-950 dark:pb-3 dark:text-sm dark:font-semibold dark:normal-case dark:tracking-normal dark:text-inherit dark:shadow-[inset_0_-1px_0_0_rgba(165,180,252,0.2)]"

const tdClass =
  "border-t border-[var(--lm-border-subtle)] px-2.5 py-2.5 align-top dark:border-t-0 dark:py-3 dark:pr-4"

function handleRowDragStart(e: React.DragEvent, row: ReviewRow) {
  e.dataTransfer.effectAllowed = 'copy'
  e.dataTransfer.setData(REVIEW_SECTION_MIME, JSON.stringify(row))
  e.dataTransfer.setData('text/plain', `${row.section} (Page ${row.pageNumber})`)
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-500">
      {children}
    </p>
  )
}

function suggestedWordingDisplay(row: ReviewRow): string {
  const text = row.suggestedWording?.trim()
  if (text) return text
  return row.riskLevel === 'acceptable' ? 'N/A' : '—'
}

function ReviewRowCard({
  row,
  isOpen,
  onToggle,
}: {
  row: ReviewRow
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <article
      draggable
      onDragStart={(e) => handleRowDragStart(e, row)}
      className="cursor-grab rounded-xl border border-[var(--lm-border-card)] bg-white shadow-[var(--shadow-card)] active:cursor-grabbing dark:border-white/10 dark:bg-slate-900/50 dark:shadow-none"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-start justify-between gap-3 p-4 text-left"
      >
        <div className="flex min-w-0 items-start gap-2">
          <GripVertical
            className="mt-0.5 h-4 w-4 shrink-0 text-[var(--lm-muted)] dark:text-slate-500"
            aria-hidden
          />
          <div className="min-w-0">
            <p className="font-medium text-[var(--lm-accent)] dark:text-violet-300">{row.section}</p>
            <p className="mt-0.5 text-[11px] text-[var(--lm-muted)] dark:text-xs dark:text-slate-400">
              Page {row.pageNumber}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <RiskBadge level={row.riskLevel} />
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-[var(--lm-muted)] dark:text-slate-400" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 text-[var(--lm-muted)] dark:text-slate-400" aria-hidden />
          )}
        </div>
      </button>

      {isOpen ? (
        <div className="space-y-3 border-t border-[var(--lm-border-subtle)] px-4 pb-4 pt-3 dark:border-white/10">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Modified by</FieldLabel>
              <p className="mt-0.5 text-sm text-[var(--lm-foreground)] dark:text-slate-200">
                {row.modifiedBy || '-'}
              </p>
            </div>
            <div>
              <FieldLabel>When</FieldLabel>
              <p className="mt-0.5 text-xs text-[var(--lm-muted)] dark:text-slate-400">
                {row.modifiedWhen || '-'}
              </p>
            </div>
          </div>

          <div>
            <FieldLabel>Change rationale</FieldLabel>
            <p className="mt-0.5 text-sm italic text-[var(--lm-muted)] dark:text-gray-400">
              {row.changeRationale || '-'}
            </p>
          </div>

          <div>
            <FieldLabel>Suggested action</FieldLabel>
            <p className="mt-0.5 text-xs text-[var(--lm-foreground)] dark:text-slate-200">
              {row.suggestedAction || 'N/A'}
            </p>
            {row.actionRationale ? (
              <p className="mt-1 text-xs text-[var(--lm-muted)] dark:text-slate-400">
                {row.actionRationale.substring(0, 60)}
                {row.actionRationale.length > 60 ? '...' : ''}
              </p>
            ) : null}
          </div>

          <div>
            <FieldLabel>Suggested wording</FieldLabel>
            <div
              className="mt-0.5 text-xs leading-relaxed text-[var(--lm-foreground)] dark:text-slate-300"
              dangerouslySetInnerHTML={{ __html: formatWording(suggestedWordingDisplay(row)) }}
            />
          </div>

          <div>
            <FieldLabel>Comment for counterparty</FieldLabel>
            <p className="mt-0.5 text-sm italic text-[var(--comment-fg)] dark:text-yellow-400">
              {row.suggestedComment || '-'}
            </p>
          </div>
        </div>
      ) : null}
    </article>
  )
}

export function ReviewTable({ rows }: ReviewTableProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <>
      <div className="hidden min-w-0 md:block">
        <div className="max-w-full rounded-xl border border-[var(--lm-border-subtle)] bg-white shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-slate-950/40 dark:shadow-none">
          <table className="w-full min-w-[1000px] border-separate border-spacing-0 text-xs leading-relaxed text-[var(--lm-foreground)] dark:text-inherit">
            <thead>
              <tr className="text-left">
                <th className={`${thClass} pr-4 w-40`}>Section</th>
                <th className={`${thClass} pr-4 w-24`}>Modified By</th>
                <th className={`${thClass} pr-4 w-24`}>When</th>
                <th className={`${thClass} pr-4 w-32`}>Change Rationale</th>
                <th className={`${thClass} pr-4 w-20`}>Risk</th>
                <th className={`${thClass} pr-4 w-48`}>Suggested Action</th>
                <th className={`${thClass} pr-4 w-56`}>Suggested Wording</th>
                <th className={`${thClass} w-40`}>Comment for Counterparty</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={index}
                  draggable
                  onDragStart={(e) => handleRowDragStart(e, row)}
                  className="cursor-grab active:cursor-grabbing even:bg-[var(--lm-row-stripe)] hover:bg-[var(--lm-row-hover)] dark:hover:bg-white/[0.04]"
                >
                  <td className={tdClass}>
                    <div className="flex items-start gap-2">
                      <GripVertical
                        className="mt-0.5 h-4 w-4 shrink-0 text-[var(--lm-muted)] dark:text-slate-500"
                        aria-hidden
                      />
                      <div>
                        <div className="text-[13px] font-medium text-[var(--lm-accent)] hover:underline dark:text-violet-300">
                          {row.section}
                        </div>
                        <div className="text-[11px] text-[var(--lm-muted)] dark:text-xs dark:text-slate-400">
                          Page {row.pageNumber}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={`${tdClass} text-sm text-[var(--lm-foreground)] dark:text-inherit`}>
                    {row.modifiedBy || '-'}
                  </td>
                  <td className={`${tdClass} text-[var(--lm-muted)] dark:text-xs dark:text-slate-400`}>
                    {row.modifiedWhen || '-'}
                  </td>
                  <td className={tdClass}>
                    <span className="text-[var(--lm-muted)] italic dark:text-sm dark:text-gray-400">
                      {row.changeRationale || '-'}
                    </span>
                  </td>
                  <td className={tdClass}>
                    <RiskBadge level={row.riskLevel} />
                  </td>
                  <td className={tdClass}>
                    <div className="text-xs">{row.suggestedAction || 'N/A'}</div>
                    {row.actionRationale && (
                      <div className="mt-1 text-[var(--lm-muted)] dark:text-xs dark:text-slate-400">
                        {row.actionRationale.substring(0, 60)}
                        {row.actionRationale.length > 60 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td className={tdClass}>
                    <div
                      className="text-xs leading-relaxed dark:text-inherit"
                      dangerouslySetInnerHTML={{ __html: formatWording(suggestedWordingDisplay(row)) }}
                    />
                  </td>
                  <td className={`${tdClass} dark:py-3`}>
                    <span className="text-sm italic text-[var(--comment-fg)] dark:text-yellow-400">
                      {row.suggestedComment || '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3 md:hidden">
        {rows.map((row, index) => (
          <ReviewRowCard
            key={index}
            row={row}
            isOpen={openIndex === index}
            onToggle={() => setOpenIndex((prev) => (prev === index ? null : index))}
          />
        ))}
      </div>
    </>
  )
}
